import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Gift, AlertTriangle } from "lucide-react";

import { EmptyState } from "~/components/ui/empty-state";
import { cn } from "~/lib/cn";
import { requirePermission } from "~/lib/functions/admin-access";
import {
  referralRewardsQueryOptions,
  type ReferralRewardRow,
} from "~/lib/functions/referral-rewards-admin";
import { formatPrice } from "~/lib/shared/format-price";

export const Route = createFileRoute("/admin/referral-rewards/")({
  staticData: { title: "Referral Rewards" },
  beforeLoad: ({ context }) =>
    requirePermission(context.adminPermissions, "coaches:read"),
  loader: async ({ context }) => {
    const rewards = await context.queryClient.ensureQueryData(
      referralRewardsQueryOptions,
    );
    return { rewards };
  },
  component: ReferralRewardsAdminPage,
});

type RewardStatus = ReferralRewardRow["status"];

function formatDate(date: Date | string | null): string {
  if (!date) return "-";
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "Invalid";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const STATUS_LABELS: Record<RewardStatus, string> = {
  pending: "Pending",
  granting: "Granting",
  granted: "Granted",
  clawed_back: "Clawed back",
};

function StatusBadge({ status }: { status: RewardStatus }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
        status === "granted" && "bg-emerald-100 text-emerald-700",
        status === "pending" && "bg-blue-100 text-blue-700",
        status === "granting" && "bg-amber-100 text-amber-700",
        status === "clawed_back" && "bg-gray-100 text-gray-600",
      )}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

function CoachCell({
  name,
  email,
}: {
  name: string | null;
  email: string | null;
}) {
  if (!name && !email) {
    return <span className="text-xs text-gray-400 italic">deleted</span>;
  }
  return (
    <div>
      <div className="font-medium text-gray-900">{name ?? "—"}</div>
      <div className="text-xs text-gray-500">{email ?? "—"}</div>
    </div>
  );
}

function ProductBadge({ product }: { product: string }) {
  return (
    <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700 capitalize">
      {product}
    </span>
  );
}

function FingerprintFlag() {
  return (
    <span
      title="Referrer and referred coach share a Stripe payment-method fingerprint — review for abuse."
      className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700"
    >
      <AlertTriangle className="h-3 w-3" />
      Fingerprint match
    </span>
  );
}

function ReferralRewardsAdminPage() {
  const { data: rewards, isLoading } = useQuery(referralRewardsQueryOptions);

  const flaggedCount =
    rewards?.filter((r) => r.paymentMethodFingerprintMatch === true).length ??
    0;
  const grantedCents =
    rewards
      ?.filter((r) => r.status === "granted")
      .reduce((total, r) => total + r.amountCents, 0) ?? 0;

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="mb-2 text-2xl font-medium text-gray-900">
        Referral Rewards
      </h1>
      <p className="mb-6 text-sm text-gray-500">
        Audit of double-sided referral credit. Rows flagged with a fingerprint
        match share a Stripe payment method and warrant manual review.
      </p>

      {/* Summary */}
      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-8">
          <div>
            <div className="text-2xl font-medium text-gray-900">
              {rewards?.length ?? 0}
            </div>
            <div className="text-xs text-gray-500">Total rewards</div>
          </div>
          <div>
            <div className="text-2xl font-medium text-gray-900">
              {formatPrice(grantedCents, "USD")}
            </div>
            <div className="text-xs text-gray-500">Granted credit</div>
          </div>
          <div>
            <div
              className={cn(
                "text-2xl font-medium",
                flaggedCount > 0 ? "text-red-600" : "text-gray-900",
              )}
            >
              {flaggedCount}
            </div>
            <div className="text-xs text-gray-500">Fingerprint flags</div>
          </div>
        </div>
      </div>

      {/* Rewards Table */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <h2 className="border-b px-6 py-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
          All Rewards
        </h2>
        {!rewards?.length ? (
          <EmptyState
            size="sm"
            icon={Gift}
            title="No referral rewards"
            description="Rewards appear here once a referred coach makes their first qualifying payment."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-left text-xs tracking-wider text-gray-500 uppercase">
                  <th className="px-4 py-3">Referrer</th>
                  <th className="px-4 py-3">Referred</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Matures</th>
                  <th className="px-4 py-3">Granted</th>
                  <th className="px-4 py-3">Clawed back</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3">Review</th>
                </tr>
              </thead>
              <tbody>
                {rewards.map((row) => (
                  <tr
                    key={row.id}
                    className={cn(
                      "border-b last:border-0 hover:bg-gray-50",
                      row.paymentMethodFingerprintMatch === true && "bg-red-50",
                    )}
                  >
                    <td className="px-4 py-3">
                      <CoachCell
                        name={row.referrerName}
                        email={row.referrerEmail}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <CoachCell
                        name={row.referredName}
                        email={row.referredEmail}
                      />
                    </td>
                    <td className="px-4 py-3 font-medium whitespace-nowrap text-gray-900">
                      {formatPrice(row.amountCents, "USD")}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={row.status} />
                    </td>
                    <td className="px-4 py-3">
                      <ProductBadge product={row.qualifyingProduct} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                      {formatDate(row.maturesAt)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                      {formatDate(row.grantedAt)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                      {formatDate(row.clawedBackAt)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                      {formatDate(row.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      {row.paymentMethodFingerprintMatch === true ? (
                        <FingerprintFlag />
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
